import {
  parseLocations,
  filterLocations,
  sortByDistance,
  withinRadius,
  stateLabel,
  sortByStateLabel,
} from "al-locator-core";

const PIN_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z"/></svg>';

// Cluster bubbles as plain solid circles (no translucent halo/"wave" rings).
const CLUSTER_RENDERER = {
  render({ count, position }) {
    const size = count < 10 ? 38 : count < 50 ? 46 : 54;
    const svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="' + size + '" height="' + size +
      '" viewBox="0 0 ' + size + " " + size +
      '"><circle cx="' + size / 2 + '" cy="' + size / 2 + '" r="' + size / 2 +
      '" fill="#1a6fd6"/></svg>';
    return new google.maps.Marker({
      position,
      icon: {
        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(size / 2, size / 2),
      },
      label: {
        text: String(count),
        color: "#ffffff",
        fontSize: "13px",
        fontWeight: "700",
      },
      zIndex: 1000 + count,
    });
  },
};

function readJson(id) {
  const elx = document.getElementById(id);
  if (!elx) return null;
  try {
    return JSON.parse(elx.textContent);
  } catch {
    return null;
  }
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[c]));
}

// Human label from the derived categories (the raw service-type text isn't stored).
function serviceLabel(categories) {
  const cats = Array.isArray(categories) ? categories : [];
  const parts = [
    ["diagnostic", "Diagnostic"],
    ["therapy", "Therapy"],
    ["diabetes", "Diabetes"],
  ]
    .filter(([key]) => cats.includes(key))
    .map(([, label]) => label);
  if (parts.length === 0) return "Sleep Services";
  const joined =
    parts.length <= 2
      ? parts.join(" & ")
      : parts.slice(0, -1).join(", ") + " & " + parts[parts.length - 1];
  return joined + " Services";
}

function loadGoogleMaps(key) {
  if (window.google && window.google.maps) return Promise.resolve();
  if (window.__alLocatorMapsPromise) return window.__alLocatorMapsPromise;
  window.__alLocatorMapsPromise = new Promise((resolve, reject) => {
    const callbackName = "__alLocatorMapsInit";
    window[callbackName] = () => resolve();
    const script = document.createElement("script");
    script.src =
      "https://maps.googleapis.com/maps/api/js?key=" +
      encodeURIComponent(key) +
      "&libraries=places&loading=async&callback=" +
      callbackName;
    script.async = true;
    script.onerror = () => reject(new Error("Google Maps failed to load"));
    document.head.appendChild(script);
  });
  return window.__alLocatorMapsPromise;
}

// Marker clustering library (numbered cluster bubbles). Non-fatal if it fails.
function loadClusterer() {
  if (window.markerClusterer) return Promise.resolve();
  if (window.__alClustererPromise) return window.__alClustererPromise;
  window.__alClustererPromise = new Promise((resolve) => {
    const script = document.createElement("script");
    script.src =
      "https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js";
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
  return window.__alClustererPromise;
}

function buildListItem(loc) {
  const li = el("li", "al-locator__item");
  li.dataset.id = loc.id;

  const pin = el("span", "al-locator__pin");
  pin.innerHTML = PIN_SVG; // static constant markup, no user data
  li.appendChild(pin);

  const main = el("div", "al-locator__item-main");
  main.appendChild(el("h3", "al-locator__item-title", stateLabel(loc)));
  main.appendChild(el("p", "al-locator__item-service", serviceLabel(loc.categories)));
  if (typeof loc.distanceKm === "number") {
    main.appendChild(
      el("p", "al-locator__item-distance", loc.distanceKm.toFixed(1) + " km away"),
    );
  }

  const details = el("div", "al-locator__item-details");
  if (loc.address) details.appendChild(el("p", null, loc.address));
  if (loc.phone) {
    const p = el("p", null);
    const a = el("a", null, loc.phone);
    a.href = "tel:" + loc.phone.replace(/\s+/g, "");
    p.appendChild(a);
    details.appendChild(p);
  }
  if (loc.bookingUrl && /^https?:\/\//i.test(loc.bookingUrl)) {
    const a = el("a", "al-locator__item-book", "Book now");
    a.href = loc.bookingUrl;
    a.target = "_blank";
    a.rel = "noopener";
    details.appendChild(a);
  }
  main.appendChild(details);
  li.appendChild(main);

  li.appendChild(el("span", "al-locator__chev", "›"));
  return li;
}

function infoWindowHtml(loc) {
  const parts = ["<strong>" + escapeHtml(loc.title) + "</strong>"];
  parts.push(escapeHtml(serviceLabel(loc.categories)));
  if (loc.address) parts.push(escapeHtml(loc.address));
  if (loc.phone) {
    parts.push(
      '<a href="tel:' + escapeHtml(loc.phone.replace(/\s+/g, "")) + '">' + escapeHtml(loc.phone) + "</a>",
    );
  }
  if (loc.bookingUrl && /^https?:\/\//i.test(loc.bookingUrl)) {
    parts.push(
      '<a href="' + escapeHtml(loc.bookingUrl) + '" target="_blank" rel="noopener">Book now</a>',
    );
  }
  return '<div class="al-locator__infowindow">' + parts.join("<br>") + "</div>";
}

class StoreLocator {
  constructor(root, config, locations) {
    this.root = root;
    this.config = config || {};
    this.all = locations;
    this.filtered = locations;
    this.origin = null;
    this.map = null;
    this.infoWindow = null;
    this.geocoder = null;
    this.markers = new Map();
    this.clusterer = null;
    this.circle = null;
    this.nearestExcluded = null;

    this.listEl = root.querySelector("#al-locator-list");
    this.countEl = root.querySelector("#al-locator-count");
    this.inputEl = root.querySelector("#al-locator-postcode");
    this.suggestEl = root.querySelector("#al-locator-suggestions");
    this.statusEl = root.querySelector("#al-locator-status");
    this.categoryEls = root.querySelectorAll("input[name='al-locator-category']");
    this.radiusEl = root.querySelector("#al-locator-radius");
    this.radiusValueEl = root.querySelector("#al-locator-radius-value");
    this.radiusKm = this.radiusEl ? Number(this.radiusEl.value) : 50;

    this.categoryEls.forEach((r) => r.addEventListener("change", () => this.applyFilters()));
    if (this.radiusEl) {
      this.radiusEl.addEventListener("input", () => {
        this.radiusKm = Number(this.radiusEl.value);
        if (this.radiusValueEl) this.radiusValueEl.textContent = String(this.radiusKm);
        if (this.origin) this.applyFilters();
      });
      this.radiusEl.addEventListener("change", () => {
        if (this.origin && this.circle && this.map) {
          this.map.fitBounds(this.circle.getBounds());
        }
      });
    }
  }

  currentCategory() {
    const checked = [...this.categoryEls].find((r) => r.checked);
    return checked ? checked.value : "all";
  }

  setStatus(message) {
    if (this.statusEl) this.statusEl.textContent = message;
  }

  applyFilters() {
    let result = filterLocations(this.all, {
      category: this.currentCategory(),
    });
    this.nearestExcluded = null;
    if (this.origin) {
      result = sortByDistance(result, this.origin);
      const within = withinRadius(result, this.radiusKm);
      if (within.length === 0 && result.length > 0) this.nearestExcluded = result[0];
      result = within;
    } else {
      // No search yet: group the list by state, then name.
      result = sortByStateLabel(result);
    }
    this.filtered = result;
    this.render();
    this.updateCircle();
  }

  updateCircle() {
    if (!this.map || !window.google) return;
    if (!this.origin) {
      if (this.circle) {
        this.circle.setMap(null);
        this.circle = null;
      }
      return;
    }
    if (!this.circle) {
      this.circle = new google.maps.Circle({
        map: this.map,
        clickable: false,
        strokeColor: "#1a6fd6",
        strokeOpacity: 0.5,
        strokeWeight: 1,
        fillColor: "#1a6fd6",
        fillOpacity: 0.06,
      });
    }
    this.circle.setCenter(this.origin);
    this.circle.setRadius(this.radiusKm * 1000);
  }

  // Frame the map around the searched origin (the radius circle if present).
  frameOrigin() {
    if (!this.map || !this.origin) return;
    if (this.circle) {
      this.map.fitBounds(this.circle.getBounds());
    } else {
      this.map.setCenter(this.origin);
      this.map.setZoom(9);
    }
  }

  // Places API (New) autocomplete via the Data API: we fetch suggestions in JS
  // and render our own dropdown on our own styled input (full styling control).
  // Requires "Places API (New)" enabled on the Maps key; degrades silently if
  // absent — the list and map still work.
  async setupAutocomplete() {
    if (!this.inputEl || !window.google || !google.maps.importLibrary) return;
    let places;
    try {
      places = await google.maps.importLibrary("places");
    } catch {
      return;
    }
    this.Suggestion = places.AutocompleteSuggestion;
    this.SessionToken = places.AutocompleteSessionToken;
    if (!this.Suggestion || !this.SessionToken) return;
    this.token = new this.SessionToken();
    this.predictions = [];
    this.activeIndex = -1;

    let debounce;
    this.inputEl.addEventListener("input", () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => this.fetchSuggestions(), 180);
    });
    this.inputEl.addEventListener("keydown", (e) => this.onSearchKey(e));
    this.inputEl.addEventListener("blur", () => {
      setTimeout(() => this.closeSuggestions(), 120); // allow click to register
    });
  }

  async fetchSuggestions() {
    const input = this.inputEl.value.trim();
    if (input.length < 3) {
      this.closeSuggestions();
      return;
    }
    let res;
    try {
      res = await this.Suggestion.fetchAutocompleteSuggestions({
        input,
        includedRegionCodes: ["au"],
        sessionToken: this.token,
      });
    } catch {
      this.closeSuggestions();
      return;
    }
    this.predictions = (res.suggestions || [])
      .map((s) => s.placePrediction)
      .filter(Boolean);
    this.renderSuggestions();
  }

  renderSuggestions() {
    this.suggestEl.replaceChildren();
    this.activeIndex = -1;
    if (!this.predictions.length) {
      this.closeSuggestions();
      return;
    }
    this.predictions.forEach((pred, i) => {
      const li = el("li", "al-locator__suggestion");
      li.setAttribute("role", "option");
      li.appendChild(
        el(
          "span",
          "al-locator__suggestion-main",
          pred.mainText ? pred.mainText.toString() : pred.text.toString(),
        ),
      );
      if (pred.secondaryText) {
        li.appendChild(
          el("span", "al-locator__suggestion-sub", pred.secondaryText.toString()),
        );
      }
      // mousedown (not click) so it fires before the input's blur handler.
      li.addEventListener("mousedown", (e) => {
        e.preventDefault();
        this.selectSuggestion(i);
      });
      this.suggestEl.appendChild(li);
    });
    this.suggestEl.hidden = false;
    this.inputEl.setAttribute("aria-expanded", "true");
  }

  onSearchKey(e) {
    if (this.suggestEl.hidden) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.setActive(Math.min(this.activeIndex + 1, this.predictions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      this.setActive(Math.max(this.activeIndex - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      this.selectSuggestion(this.activeIndex >= 0 ? this.activeIndex : 0);
    } else if (e.key === "Escape") {
      this.closeSuggestions();
    }
  }

  setActive(i) {
    this.activeIndex = i;
    [...this.suggestEl.children].forEach((li, idx) =>
      li.classList.toggle("is-active", idx === i),
    );
  }

  async selectSuggestion(i) {
    const pred = this.predictions[i];
    if (!pred) return;
    this.inputEl.value = pred.text.toString();
    this.closeSuggestions();
    try {
      const place = pred.toPlace();
      await place.fetchFields({ fields: ["location"] });
      const p = place.location;
      if (!p) return;
      const lat = typeof p.lat === "function" ? p.lat() : p.lat;
      const lng = typeof p.lng === "function" ? p.lng() : p.lng;
      this.origin = { lat, lng };
      this.setStatus("");
      this.applyFilters();
      this.frameOrigin();
    } catch {
      this.setStatus("Could not load that location.");
    }
    this.token = new this.SessionToken(); // renew session after a selection
  }

  closeSuggestions() {
    if (!this.suggestEl) return;
    this.suggestEl.hidden = true;
    this.suggestEl.replaceChildren();
    this.activeIndex = -1;
    this.inputEl.setAttribute("aria-expanded", "false");
  }

  async initMap() {
    await loadGoogleMaps(this.config.mapsKey);
    await loadClusterer();
    this.map = new google.maps.Map(this.root.querySelector("#al-locator-map"), {
      center: this.config.center || { lat: -25.27, lng: 133.77 },
      zoom: this.config.zoom || 4,
      mapTypeControl: false,
      streetViewControl: false,
      zoomControl: true,
      fullscreenControl: true,
    });
    // disableAutoPan: we control the map center ourselves, so the popup never
    // pans the map (avoids fighting our setCenter, esp. for clustered markers).
    this.infoWindow = new google.maps.InfoWindow({ disableAutoPan: true });
    this.setupAutocomplete();
    this.renderMarkers();
  }

  renderMarkers() {
    if (this.clusterer) this.clusterer.clearMarkers();
    this.markers.forEach((m) => m.setMap(null));
    this.markers.clear();

    const bounds = new google.maps.LatLngBounds();
    const markerList = [];
    for (const loc of this.filtered) {
      const position = { lat: loc.lat, lng: loc.lng };
      const marker = new google.maps.Marker({ position, title: loc.title });
      marker.addListener("click", () => this.select(loc.id, true));
      this.markers.set(loc.id, marker);
      markerList.push(marker);
      bounds.extend(position);
    }

    const Clusterer =
      window.markerClusterer && window.markerClusterer.MarkerClusterer;
    if (Clusterer) {
      if (!this.clusterer) {
        this.clusterer = new Clusterer({ map: this.map, renderer: CLUSTER_RENDERER });
      }
      this.clusterer.addMarkers(markerList);
    } else {
      markerList.forEach((m) => m.setMap(this.map));
    }

    if (this.filtered.length && !this.origin) this.map.fitBounds(bounds);
  }

  render() {
    this.renderList();
    if (this.countEl) {
      const n = this.filtered.length;
      this.countEl.textContent = n + (n === 1 ? " result" : " results");
    }
    if (this.map) this.renderMarkers();
  }

  renderList() {
    this.listEl.innerHTML = "";
    if (!this.filtered.length) {
      let message = "No locations match your filters.";
      if (this.nearestExcluded) {
        message =
          "No clinics within " +
          this.radiusKm +
          " km. Nearest is " +
          this.nearestExcluded.title +
          " (" +
          Math.round(this.nearestExcluded.distanceKm) +
          " km) — increase the distance to include it.";
      }
      this.listEl.appendChild(el("li", "al-locator__empty", message));
      return;
    }
    for (const loc of this.filtered) {
      const li = buildListItem(loc);
      li.addEventListener("click", () => this.select(loc.id, false));
      this.listEl.appendChild(li);
    }
  }

  select(id, fromMarker) {
    const loc = this.all.find((l) => l.id === id);
    if (!loc) return;
    this.listEl
      .querySelectorAll(".al-locator__item.is-active")
      .forEach((n) => n.classList.remove("is-active"));
    const item = this.listEl.querySelector(
      '[data-id="' + (window.CSS ? CSS.escape(id) : id) + '"]',
    );
    if (item) {
      item.classList.add("is-active");
      if (!fromMarker) item.scrollIntoView({ block: "nearest" });
    }
    if (this.map) {
      const position = { lat: loc.lat, lng: loc.lng };
      if (this.infoWindow) {
        // Anchor by explicit position (not the marker, which may be hidden in a
        // cluster and would mis-place the popup).
        this.infoWindow.close();
        this.infoWindow.setContent(infoWindowHtml(loc));
        this.infoWindow.setPosition(position);
        this.infoWindow.open({ map: this.map });
      }
      this.map.setCenter(position);
      if (this.map.getZoom() < 13) this.map.setZoom(14);
    }
  }
}

function init() {
  const root = document.querySelector("[data-al-locator]");
  if (!root) return;
  const config = readJson("al-locator-config") || {};
  const dataEl = document.getElementById("al-locator-data");
  const locations = parseLocations(dataEl ? dataEl.textContent : "[]");

  const app = new StoreLocator(root, config, locations);
  app.applyFilters(); // renders immediately, state-grouped (no map needed)

  if (config.mapsKey) {
    app.initMap().catch(() => app.setStatus("Map could not be loaded; the list is still available."));
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
